import { useCallback, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { cajaService, comandasService, configuracionService, metodosPagoService, productosService, promocionesService } from '../../services/api';
import { SOCKET_URL } from '../../utils/socket';
import { abrirTicket } from '../../utils/ticket';
import { useToast } from '../../components/ui/Toast';
import { btnSecundario } from '../../components/ui/estilos';
import ModalLlaveAcceso from '../../components/ModalLlaveAcceso';
import ModalSplitCuentas from '../../components/ModalSplitCuentas';
import TomaPedido from '../salon/TomaPedido';
import { duracion } from '../salon/mesas';
import ListaCuentas, { Filtro, estadoFila, filasDe } from './ListaCuentas';
import PanelCobro, { AccionesCuenta } from './PanelCobro';
import { DialogoCajon, DialogoCargo, DialogoDescuento, DialogoProductos, DialogoUnir, PagoExitoso } from './DialogosCuenta';
import { Metodo, Seleccion, useCobro } from './useCobro';

const METODOS_BASE: Metodo[] = [
  { value: 'efectivo', label: 'Efectivo' }, { value: 'tarjeta', label: 'Tarjeta' }, { value: 'transferencia', label: 'Transferencia' },
];
const pcts = (v: string | undefined, def: number[]) => {
  const x = (v || '').split(',').map(parseFloat).filter(p => p > 0 && p <= 100);
  return x.length ? x : def;
};

type Dialogo = 'descuento' | 'unir' | 'cargo' | 'cajon' | 'productos' | 'cancelar' | 'dividir' | 'llevar' | null;

// embebido: montada dentro de Salón para cobrar la mesa que se tocó, sin la lista.
export default function Cuentas({ mesaInicial, llevarInicial, embebido = false, onCerrar }: {
  mesaInicial?: string; llevarInicial?: any; embebido?: boolean; onCerrar?: () => void;
}) {
  const [toast, say] = useToast();
  const [metodos, setMetodos] = useState<Metodo[]>(METODOS_BASE);
  const [config, setConfig] = useState<Record<string, string> | null>(null);
  const [mesas, setMesas] = useState<any[]>([]);
  const [llevar, setLlevar] = useState<any[]>([]);
  const [filtro, setFiltro] = useState<Filtro>('cobrar');
  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [cuentaSplit, setCuentaSplit] = useState<any>(null);
  const [menu, setMenu] = useState<{ productos: any[]; promociones: any[] } | null>(null);
  const c = useCobro(metodos, config);
  const selRef = useRef<Seleccion | null>(null);
  selRef.current = c.sel;

  const cargarListas = useCallback(() => {
    cajaService.getMesasCuentas().then(r => setMesas(r.data)).catch(() => say('No se pudieron cargar las cuentas.'));
    cajaService.getParaLlevar().then(r => setLlevar(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [say]);

  useEffect(() => {
    metodosPagoService.getAll().then(r => r.data.length && setMetodos(r.data.map((m: any) => ({ value: m.clave, label: m.nombre })))).catch(() => {});
    configuracionService.get().then(r => setConfig(r.data)).catch(() => {});
    cargarListas();
    const socket = io(SOCKET_URL, { auth: { token: localStorage.getItem('token') } });
    socket.on('mesa_actualizada', cargarListas);
    socket.on('comanda_actualizada', (cm: any) => {
      cargarListas();
      const s = selRef.current;
      if (s?.tipo === 'mesa' && cm?.mesa_id === s.mesaId) c.recargar();
    });
    return () => { socket.disconnect(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (mesaInicial) c.abrir({ tipo: 'mesa', mesaId: mesaInicial, subcuenta: 'base' }); }, [mesaInicial]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (llevarInicial) c.abrir({ tipo: 'llevar', comandaId: llevarInicial.comanda_id }); }, [llevarInicial]); // eslint-disable-line react-hooks/exhaustive-deps

  const refrescar = async () => { await c.recargar(); cargarListas(); };
  const filas = filasDe(mesas, llevar);
  const cta = c.cuenta;
  const sel = c.sel;
  const fila = sel && filas.find(f => JSON.stringify(f.sel) === JSON.stringify(sel));
  const titulo = cta ? (cta.tipo === 'para_llevar' ? `Para llevar · ${cta.cliente_nombre || ''}` : `Mesa ${cta.etiqueta_cuenta || cta.mesa_numero}`) : '';

  const abrirSplit = async () => {
    if (sel?.tipo !== 'mesa') return;
    try { setCuentaSplit((await cajaService.getCuenta(sel.mesaId)).data); setDialogo('dividir'); }
    catch { say('No se pudo abrir la división de la cuenta.'); }
  };

  const unir = async (origen: any, label: string) => {
    if (!cta || !sel) return;
    if (!window.confirm(`¿Pasar todo lo de ${label} a ${titulo}? Se cobra en un solo ticket.`)) return;
    try {
      await cajaService.unirCuentas(origen, sel.tipo === 'mesa' ? { mesa_id: sel.mesaId } : { comanda_id: cta.comanda_id });
      setDialogo(null); say(`${label} se unió a ${titulo}.`); await refrescar();
    } catch (e: any) { say(e.response?.data?.error || 'No se pudieron unir las cuentas.'); }
  };

  const precuenta = () => c.precuenta().then(r => say(r === 'ok' ? 'Pre-cuenta impresa.' : 'Pre-cuenta lista para imprimir.')).catch(() => say('No se pudo imprimir la pre-cuenta.'));

  const abrirLlevar = async () => {
    if (!menu) {
      try {
        const [p, pr] = await Promise.all([productosService.getAll(), promocionesService.getAll()]);
        const dia = new Date().getDay();
        setMenu({
          productos: p.data.filter((x: any) => !x.suspendido),
          promociones: pr.data.filter((x: any) => x.activa && x.items?.length && (!x.dias_semana || x.dias_semana.split(',').map(Number).includes(dia))),
        });
      } catch { say('No se pudo cargar el menú.'); return; }
    }
    setDialogo('llevar');
  };

  const candidatosUnir = !cta ? [] : [
    ...mesas.filter(m => !(sel?.tipo === 'mesa' && m.mesa_id === sel.mesaId) && !m.dividida)
      .map(m => ({ key: m.mesa_id, label: `Mesa ${m.mesa_numero}`, total: (m.cuentas || []).reduce((s: number, x: any) => s + (Number(x.total) || 0), 0), origen: { mesa_id: m.mesa_id } })),
    ...llevar.filter(p => p.comanda_id !== cta.comanda_id)
      .map(p => ({ key: p.comanda_id, label: `Para llevar · ${p.cliente_nombre || 'Cliente'}`, total: parseFloat(p.total) || 0, origen: { comanda_id: p.comanda_id } })),
  ];

  const detalle = (
    <main style={{ minHeight: 0, overflowY: 'auto', padding: embebido ? 0 : '16px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      {embebido && <button onClick={onCerrar} style={{ ...btnSecundario, alignSelf: 'flex-start' }}>‹ {llevarInicial ? 'Volver' : 'Volver al salón'}</button>}
      {c.pagoExitoso ? (
        <PagoExitoso pago={c.pagoExitoso}
          onReimprimir={c.pagoExitoso.ticket ? () => abrirTicket(c.pagoExitoso.ticket).then(r => say(r === 'ok' ? 'Ticket enviado a la impresora.' : 'No se pudo imprimir; revisa la impresora.')) : undefined}
          onListo={() => { c.setPagoExitoso(null); if (embebido) onCerrar?.(); }} />
      ) : c.cargando ? (
        <div style={{ color: 'var(--muted)', fontSize: 17 }}>Cargando cuenta…</div>
      ) : cta ? (
        <PanelCobro c={c} metodos={metodos} pctsPropina={pcts(config?.caja_propinas_pct, [10, 15, 20])}
          encabezado={{
            label: titulo,
            sub: [cta.mesero, cta.tipo === 'para_llevar' ? 'Para llevar' : 'Mesa', fila ? duracion(fila.min) : null].filter(Boolean).join(' · '),
            estado: estadoFila({ porCobrar: fila?.porCobrar ?? true }),
            abierta: !!fila && !fila.porCobrar,
          }}
          subcuentas={sel?.tipo === 'mesa' ? (cta.cuentas || []).map((x: any) => ({
            id: x.subcuenta_id, etiqueta: x.etiqueta, total: Number(x.total) || 0, pagada: x.pagada, activa: x.subcuenta_id === sel.subcuenta,
            onClick: () => c.abrir({ tipo: 'mesa', mesaId: sel.mesaId, subcuenta: x.subcuenta_id }),
          })) : undefined}
          onDescuento={() => setDialogo('descuento')}
          onDividir={sel?.tipo === 'mesa' && (cta.items.length > 1 || cta.dividida) ? abrirSplit : undefined}
          onUnir={() => { cargarListas(); setDialogo('unir'); }}
          onPrecuenta={precuenta}
          onEditarProductos={() => setDialogo('productos')} onCargo={() => setDialogo('cargo')}
          onCajon={() => setDialogo('cajon')} onCancelar={() => setDialogo('cancelar')}
          accionesAparte={!embebido}
          onQuitarCargo={id => comandasService.deleteCargo(cta.comanda_id, id).then(refrescar).catch(() => say('No se pudo quitar el cargo.'))}
        />
      ) : (
        <div style={{ flex: 1, display: 'grid', placeItems: 'center', color: 'var(--muted)', fontSize: 17, minHeight: 240 }}>
          {c.error || 'Elige una cuenta para cobrar.'}
        </div>
      )}
    </main>
  );

  return (
    <div className="paneles" style={{ height: embebido ? '100%' : 'calc(var(--vh100, 100vh) - 124px)', minHeight: 480, margin: embebido ? 0 : '-22px -24px', display: embebido ? 'flex' : 'grid', flexDirection: 'column', gridTemplateColumns: 'clamp(280px, 30%, 380px) minmax(0,1fr)' }}>
      {!embebido && (
        <ListaCuentas filas={filas} filtro={filtro} onFiltro={setFiltro} actual={sel} onNuevoLlevar={abrirLlevar}
          onElegir={s => c.abrir(s)}
          pie={cta && !c.pagoExitoso && !c.cargando ? <AccionesCuenta onEditarProductos={() => setDialogo('productos')} onCargo={() => setDialogo('cargo')}
            onCajon={() => setDialogo('cajon')} onCancelar={() => setDialogo('cancelar')} /> : undefined} />
      )}
      {detalle}

      {dialogo === 'descuento' && cta && <DialogoDescuento c={c} pcts={pcts(config?.caja_descuentos_pct, [10, 15, 20])} onCerrar={() => setDialogo(null)} />}
      {dialogo === 'unir' && cta && <DialogoUnir titulo={titulo} candidatos={candidatosUnir} onElegir={unir} onCerrar={() => setDialogo(null)} />}
      {dialogo === 'cargo' && cta && <DialogoCargo comandaId={cta.comanda_id} onCerrar={() => setDialogo(null)} onListo={() => { setDialogo(null); say('Cargo agregado.'); refrescar(); }} />}
      {dialogo === 'cajon' && <DialogoCajon onCerrar={() => setDialogo(null)} />}
      {dialogo === 'productos' && cta && <DialogoProductos cuenta={cta} onCambio={refrescar} onCerrar={() => setDialogo(null)} />}
      {dialogo === 'cancelar' && cta && (
        <ModalLlaveAcceso permiso="puede_cancelar_cuenta" titulo={`Cancelar ${titulo}`}
          descripcion="Se libera y NO cuenta como venta."
          onCancel={() => setDialogo(null)}
          onSuccess={() => {
            setDialogo(null);
            cajaService.cancelarCuenta(cta.comanda_id)
              .then(() => { say(`${titulo} cancelada.`); c.cerrar(); cargarListas(); if (embebido) onCerrar?.(); })
              .catch((e: any) => say(e.response?.data?.error || 'No se pudo cancelar la cuenta.'));
          }} />
      )}
      {dialogo === 'dividir' && cuentaSplit && (
        <ModalSplitCuentas cuenta={cuentaSplit} onClose={() => setDialogo(null)}
          onActualizada={async () => { if (sel?.tipo === 'mesa') setCuentaSplit((await cajaService.getCuenta(sel.mesaId)).data); await refrescar(); }} />
      )}
      {dialogo === 'llevar' && menu && (
        <TomaPedido titulo="Para llevar" destino={{ tipo: 'llevar', comandaId: null }} productos={menu.productos} promociones={menu.promociones}
          onCerrar={() => setDialogo(null)} onEnviado={m => { setDialogo(null); say(m); cargarListas(); }} />
      )}
      {toast}
    </div>
  );
}
